import React, { useState } from "react";
import {
    useGetAdvFundSettingsQuery,
    useUpdateAdvFundSettingsMutation,
    useGetAdvFundMonthlyStatsQuery,
    useGetAdvFundHistoricalStatsQuery,
    useGetAdvFundUsersQuery,
    useGetAdvFundPaymentsQuery,
    useGetAdvFundChannelsQuery,
    useCreateAdvFundChannelMutation,
    useUpdateAdvFundChannelMutation,
    useDeleteAdvFundChannelMutation,
    useGetAdvFundExpensesQuery,
    useCreateAdvFundExpenseMutation,
} from "../api";
import { AdvFundUsersParams, AdvFundPaymentsParams, AdvFundExpensesParams } from "../api/advertisingFund";
import { SettingsIcon, HistoryIcon, PlusIcon, Copy } from "lucide-react";
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";

type Tab = "users" | "payments" | "channels" | "expenses";

const AdvertisingFundPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<Tab>("users");

    // Settings
    const { data: settings } = useGetAdvFundSettingsQuery();
    const [minAmount, setMinAmount] = useState<string>("");
    const [settingsSaved, setSettingsSaved] = useState(false);
    const [updateSettings, { isLoading: isSavingSettings }] = useUpdateAdvFundSettingsMutation();

    React.useEffect(() => {
        if (settings) {
            setMinAmount(String(settings.min_amount));
        }
    }, [settings]);

    const handleSaveSettings = async () => {
        try {
            await updateSettings({ min_amount: parseFloat(minAmount) }).unwrap();
            setSettingsSaved(true);
            setTimeout(() => setSettingsSaved(false), 2000);
        } catch (err) {
            console.error("Failed to save settings", err);
        }
    };

    // Stats
    const { data: monthlyStats } = useGetAdvFundMonthlyStatsQuery();
    const { data: historicalData } = useGetAdvFundHistoricalStatsQuery();

    // Users tab
    const [usersParams, setUsersParams] = useState<AdvFundUsersParams>({
        page: 1,
        per_page: 20,
        search: "",
        status: "",
    });
    const { data: usersData, isLoading: usersLoading } = useGetAdvFundUsersQuery(usersParams);

    // Payments tab
    const [paymentsParams, setPaymentsParams] = useState<AdvFundPaymentsParams>({
        page: 1,
        per_page: 20,
        type: "",
        date_from: "",
        date_to: "",
    });
    const { data: paymentsData, isLoading: paymentsLoading } = useGetAdvFundPaymentsQuery(paymentsParams);

    // Channels tab
    const { data: channelsData, isLoading: channelsLoading } = useGetAdvFundChannelsQuery();
    const [createChannel] = useCreateAdvFundChannelMutation();
    const [updateChannel] = useUpdateAdvFundChannelMutation();
    const [deleteChannel] = useDeleteAdvFundChannelMutation();
    const [newChannelName, setNewChannelName] = useState("");
    const [newChannelDesc, setNewChannelDesc] = useState("");
    const [channelLoading, setChannelLoading] = useState(false);

    const handleCreateChannel = async () => {
        if (!newChannelName.trim()) return;
        setChannelLoading(true);
        try {
            await createChannel({ name: newChannelName, description: newChannelDesc, is_active: true }).unwrap();
            setNewChannelName("");
            setNewChannelDesc("");
        } catch (err) {
            console.error("Failed to create channel", err);
        } finally {
            setChannelLoading(false);
        }
    };

    const handleToggleChannel = async (id: number, is_active: boolean) => {
        try {
            await updateChannel({ id, is_active: !is_active }).unwrap();
        } catch (err) {
            console.error("Failed to toggle channel", err);
        }
    };

    const handleDeleteChannel = async (id: number) => {
        if (!window.confirm("Удалить канал?")) return;
        try {
            await deleteChannel(id).unwrap();
        } catch (err) {
            console.error("Failed to delete channel", err);
        }
    };

    // Expenses tab
    const [expensesParams, setExpensesParams] = useState<AdvFundExpensesParams>({
        page: 1,
        per_page: 20,
    });
    const { data: expensesData, isLoading: expensesLoading } = useGetAdvFundExpensesQuery(expensesParams);
    const [createExpense] = useCreateAdvFundExpenseMutation();
    const [expChannelId, setExpChannelId] = useState<string>("");
    const [expAmount, setExpAmount] = useState<string>("");
    const [expDate, setExpDate] = useState<string>(new Date().toISOString().split("T")[0]);
    const [expDesc, setExpDesc] = useState<string>("");
    const [expRegs, setExpRegs] = useState<string>("0");
    const [expSaving, setExpSaving] = useState(false);

    const handleCreateExpense = async () => {
        if (!expChannelId || !expAmount || !expDate || !expDesc) return;
        setExpSaving(true);
        try {
            await createExpense({
                channelId: parseInt(expChannelId),
                amount: parseFloat(expAmount),
                expense_date: expDate,
                description: expDesc,
                registrations_count: parseInt(expRegs) || 0,
            }).unwrap();
            setExpAmount("");
            setExpDesc("");
            setExpRegs("0");
            alert("Расход добавлен");
        } catch (err) {
            console.error("Failed to create expense", err);
            alert("Ошибка при добавлении расхода");
        } finally {
            setExpSaving(false);
        }
    };

    const formatAmount = (v: number | string) =>
        Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const formatDate = (d: string) => new Date(d).toLocaleDateString("ru-RU");

    const chartData = historicalData?.data ?? [];

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-bold text-gray-900">Рекламная кубышка</h1>

            {/* ─── Monthly Stats ──────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                    <p className="text-sm text-purple-600">Участников</p>
                    <p className="text-2xl font-bold text-purple-900">{monthlyStats?.total_users ?? 0}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-sm text-blue-600">Собрано всего</p>
                    <p className="text-2xl font-bold text-blue-900">${formatAmount(monthlyStats?.total_collected ?? 0)}</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <p className="text-sm text-green-600">Лидов получено</p>
                    <p className="text-2xl font-bold text-green-900">{monthlyStats?.total_leads ?? 0}</p>
                </div>
            </div>

            {/* ─── Settings ────────────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border p-5 space-y-3">
                <div className="flex items-center gap-2 font-semibold text-gray-700">
                    <SettingsIcon className="w-4 h-4" />
                    Настройки
                </div>
                <div className="flex items-center gap-3">
                    <label className="text-sm text-gray-600 w-40">Мин. взнос ($)</label>
                    <input
                        type="number"
                        value={minAmount}
                        onChange={(e) => setMinAmount(e.target.value)}
                        className="border rounded px-3 py-1.5 text-sm w-28"
                        min={1}
                    />
                    <button
                        onClick={handleSaveSettings}
                        disabled={isSavingSettings}
                        className="bg-purple-600 text-white px-4 py-1.5 rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                    >
                        {settingsSaved ? "✓ Сохранено" : "Сохранить"}
                    </button>
                </div>
            </div>

            {/* ─── Historical Chart ─────────────────────────────────────────────── */}
            {chartData.length > 0 && (
                <div className="bg-white rounded-xl border p-5">
                    <p className="font-semibold text-gray-700 mb-4">История по месяцам</p>
                    <ResponsiveContainer width="100%" height={260}>
                        <ComposedChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" />
                            <YAxis yAxisId="left" />
                            <YAxis yAxisId="right" orientation="right" />
                            <Tooltip />
                            <Legend />
                            <Bar yAxisId="left" dataKey="total_collected" name="Собрано ($)" fill="#7c3aed" />
                            <Line yAxisId="right" type="monotone" dataKey="total_users" name="Участников" stroke="#3b82f6" dot={false} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* ─── Tabs ─────────────────────────────────────────────────────────── */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
                {(["users", "payments", "channels", "expenses"] as Tab[]).map((tab) => {
                    const labels: Record<Tab, string> = { 
                        users: "Участники", 
                        payments: "Платежи", 
                        channels: "Каналы",
                        expenses: "Расходы"
                    };
                    return (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                                activeTab === tab ? "bg-white shadow text-purple-700" : "text-gray-500 hover:text-gray-700"
                            }`}
                        >
                            {labels[tab]}
                        </button>
                    );
                })}
            </div>

            {/* ─── Users Tab ───────────────────────────────────────────────────── */}
            {activeTab === "users" && (
                <div className="bg-white rounded-xl border overflow-hidden">
                    <div className="flex gap-3 items-center px-4 py-3 border-b">
                        <input
                            type="text"
                            placeholder="Поиск..."
                            value={usersParams.search}
                            onChange={(e) => setUsersParams((p) => ({ ...p, search: e.target.value, page: 1 }))}
                            className="border rounded px-3 py-1.5 text-sm w-64"
                        />
                        <select
                            value={usersParams.status}
                            onChange={(e) => setUsersParams((p) => ({ ...p, status: e.target.value as any, page: 1 }))}
                            className="border rounded px-3 py-1.5 text-sm"
                        >
                            <option value="">Все</option>
                            <option value="active">Активные</option>
                            <option value="deleted">Удалённые</option>
                        </select>
                    </div>
                    {usersLoading ? (
                        <div className="text-center py-10 text-gray-400">Загрузка...</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-left">
                                <tr>
                                    <th className="px-4 py-3">Пользователь</th>
                                    <th className="px-4 py-3">Cookie</th>
                                    <th className="px-4 py-3">Автоплатёж</th>
                                    <th className="px-4 py-3">Итого</th>
                                    <th className="px-4 py-3">Доля %</th>
                                    <th className="px-4 py-3">Лиды</th>
                                    <th className="px-4 py-3">Статус</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {usersData?.data.map((u) => (
                                    <tr key={u.id} className={u.deleted_at ? "opacity-50" : ""}>
                                        <td className="px-4 py-3">
                                            <p className="font-medium">{u.user?.name} {u.user?.surname}</p>
                                            <p className="text-xs text-gray-400">{u.user?.email}</p>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs">{u.user?.secret_cookie}</td>
                                        <td className="px-4 py-3">${u.autopay_amount} {u.autopay ? "✓" : "✗"}</td>
                                        <td className="px-4 py-3">${formatAmount(u.total?.total_paid ?? 0)}</td>
                                        <td className="px-4 py-3">{u.total?.share_percent ?? 0}%</td>
                                        <td className="px-4 py-3">{u.total?.leads_count ?? 0}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded-full text-xs ${u.deleted_at ? "bg-red-100 text-red-600" : "bg-green-100 text-green-700"}`}>
                                                {u.deleted_at ? "Удалён" : "Активен"}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {/* Pagination */}
                    {(usersData?.last_page ?? 1) > 1 && (
                        <div className="flex justify-between items-center px-4 py-3 border-t text-sm">
                            <button onClick={() => setUsersParams((p) => ({ ...p, page: (p.page ?? 1) - 1 }))} disabled={(usersParams.page ?? 1) <= 1} className="px-3 py-1 border rounded disabled:opacity-40">←</button>
                            <span>Стр. {usersParams.page} / {usersData?.last_page}</span>
                            <button onClick={() => setUsersParams((p) => ({ ...p, page: (p.page ?? 1) + 1 }))} disabled={(usersParams.page ?? 1) >= (usersData?.last_page ?? 1)} className="px-3 py-1 border rounded disabled:opacity-40">→</button>
                        </div>
                    )}
                </div>
            )}

            {/* ─── Payments Tab ─────────────────────────────────────────────────── */}
            {activeTab === "payments" && (
                <div className="bg-white rounded-xl border overflow-hidden">
                    <div className="flex gap-3 items-center flex-wrap px-4 py-3 border-b">
                        <select
                            value={paymentsParams.type}
                            onChange={(e) => setPaymentsParams((p) => ({ ...p, type: e.target.value as any, page: 1 }))}
                            className="border rounded px-3 py-1.5 text-sm"
                        >
                            <option value="">Все типы</option>
                            <option value="auto">Автоплатёж</option>
                            <option value="manual">Ручной</option>
                        </select>
                        <input type="date" value={paymentsParams.date_from} onChange={(e) => setPaymentsParams((p) => ({ ...p, date_from: e.target.value, page: 1 }))} className="border rounded px-3 py-1.5 text-sm" />
                        <input type="date" value={paymentsParams.date_to} onChange={(e) => setPaymentsParams((p) => ({ ...p, date_to: e.target.value, page: 1 }))} className="border rounded px-3 py-1.5 text-sm" />
                    </div>
                    {paymentsLoading ? (
                        <div className="text-center py-10 text-gray-400">Загрузка...</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-left">
                                <tr>
                                    <th className="px-4 py-3">Пользователь</th>
                                    <th className="px-4 py-3">Сумма</th>
                                    <th className="px-4 py-3">Валюта</th>
                                    <th className="px-4 py-3">Тип</th>
                                    <th className="px-4 py-3">Дата</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {paymentsData?.data.map((p) => (
                                    <tr key={p.id}>
                                        <td className="px-4 py-3">
                                            <p className="font-medium">{p.user?.name} {p.user?.surname}</p>
                                            <p className="text-xs text-gray-400 font-mono">{p.user?.secret_cookie}</p>
                                        </td>
                                        <td className="px-4 py-3 font-semibold">${formatAmount(p.amount)}</td>
                                        <td className="px-4 py-3">{p.currency}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded-full text-xs ${p.type === "auto" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
                                                {p.type === "auto" ? "Авто" : "Ручной"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-500">{formatDate(p.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {(paymentsData?.last_page ?? 1) > 1 && (
                        <div className="flex justify-between items-center px-4 py-3 border-t text-sm">
                            <button onClick={() => setPaymentsParams((p) => ({ ...p, page: (p.page ?? 1) - 1 }))} disabled={(paymentsParams.page ?? 1) <= 1} className="px-3 py-1 border rounded disabled:opacity-40">←</button>
                            <span>Стр. {paymentsParams.page} / {paymentsData?.last_page}</span>
                            <button onClick={() => setPaymentsParams((p) => ({ ...p, page: (p.page ?? 1) + 1 }))} disabled={(paymentsParams.page ?? 1) >= (paymentsData?.last_page ?? 1)} className="px-3 py-1 border rounded disabled:opacity-40">→</button>
                        </div>
                    )}
                </div>
            )}

            {/* ─── Channels Tab ─────────────────────────────────────────────────── */}
            {activeTab === "channels" && (
                <div className="space-y-4">
                    {/* Create channel */}
                    <div className="bg-white rounded-xl border p-4 space-y-3">
                        <p className="font-semibold text-sm text-gray-700">Новый канал</p>
                        <div className="flex gap-3 flex-wrap items-center">
                            <input
                                type="text"
                                placeholder="Название *"
                                value={newChannelName}
                                onChange={(e) => setNewChannelName(e.target.value)}
                                className="border rounded px-3 py-1.5 text-sm w-52"
                            />
                            <input
                                type="text"
                                placeholder="Описание (опц.)"
                                value={newChannelDesc}
                                onChange={(e) => setNewChannelDesc(e.target.value)}
                                className="border rounded px-3 py-1.5 text-sm w-64"
                            />
                            <button
                                onClick={handleCreateChannel}
                                disabled={channelLoading || !newChannelName.trim()}
                                className="bg-purple-600 text-white px-4 py-1.5 rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                            >
                                Создать
                            </button>
                        </div>
                    </div>

                    {/* Channel list */}
                    {channelsLoading ? (
                        <div className="text-center py-8 text-gray-400">Загрузка...</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {(Array.isArray(channelsData) ? channelsData : (channelsData as any)?.data ?? []).map((ch: any) => {
                                const convRate = ch.conversion_rate ?? 0;
                                return (
                                <div key={ch.id} className="bg-white rounded-xl border p-4 space-y-3">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="font-semibold">{ch.name}</p>
                                            {ch.description && <p className="text-xs text-gray-500">{ch.description}</p>}
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleToggleChannel(ch.id, ch.is_active)}
                                                className={`text-xs px-2 py-1 rounded ${ch.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                                            >
                                                {ch.is_active ? "Активен" : "Неактивен"}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteChannel(ch.id)}
                                                className="text-xs px-2 py-1 rounded bg-red-100 text-red-600 hover:bg-red-200"
                                            >
                                                Удалить
                                            </button>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
                                        <div className="bg-gray-50 p-2 rounded-lg">
                                            <p className="text-gray-500">Клики (всего)</p>
                                            <p className="font-bold text-gray-900">{ch.click_logs_count ?? 0}</p>
                                        </div>
                                        <div className="bg-gray-50 p-2 rounded-lg">
                                            <p className="text-gray-500">Клики (уник)</p>
                                            <p className="font-bold text-gray-900">{ch.unique_clicks_count ?? 0}</p>
                                        </div>
                                        <div className="bg-gray-50 p-2 rounded-lg">
                                            <p className="text-gray-500">Регистрации</p>
                                            <p className="font-bold text-gray-900">{ch.registration_logs_count ?? 0}</p>
                                        </div>
                                        <div className={`p-2 rounded-lg ${convRate > 5 ? 'bg-green-50' : 'bg-blue-50'}`}>
                                            <p className={convRate > 5 ? 'text-green-600' : 'text-blue-600'}>Конверсия</p>
                                            <p className={`font-bold ${convRate > 5 ? 'text-green-900' : 'text-blue-900'}`}>{convRate}%</p>
                                        </div>
                                    </div>

                                    <div className="text-[11px] text-gray-400 pt-1 border-t flex justify-between items-center">
                                        <div className="flex items-center gap-1">
                                            <span>Ссылка:</span>
                                            <div className="flex items-center gap-1.5 bg-gray-50 px-2 py-0.5 rounded border border-gray-100">
                                                <span className="font-mono font-semibold text-purple-700 truncate max-w-[150px]">
                                                    {process.env.REACT_APP_CLIENT_CABINET}/?ref={ch.ref_code}
                                                </span>
                                                <button 
                                                    onClick={() => {
                                                        const url = `${process.env.REACT_APP_CLIENT_CABINET}/?ref=${ch.ref_code}`;
                                                        navigator.clipboard.writeText(url);
                                                        alert("Ссылка скопирована");
                                                    }}
                                                    className="text-gray-400 hover:text-purple-600"
                                                    title="Копировать"
                                                >
                                                    <Copy className="w-3 h-3" />
                                                </button>
                                            </div>
                                        </div>
                                        <span>Затраты: <span className="font-semibold text-gray-600">${formatAmount(ch.total_paid)}</span></span>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
            {/* ─── Expenses Tab ──────────────────────────────────────────────────── */}
            {activeTab === "expenses" && (
                <div className="space-y-4">
                    {/* Add expense form */}
                    <div className="bg-white rounded-xl border p-5 space-y-4">
                        <div className="flex items-center gap-2 font-semibold text-gray-700">
                            <PlusIcon className="w-4 h-4" />
                            Добавить расход
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                            <div className="space-y-1">
                                <label className="text-xs text-gray-500">Канал *</label>
                                <select 
                                    value={expChannelId} 
                                    onChange={(e) => setExpChannelId(e.target.value)}
                                    className="w-full border rounded px-3 py-1.5 text-sm"
                                >
                                    <option value="">Выберите канал...</option>
                                    {(Array.isArray(channelsData) ? channelsData : (channelsData as any)?.data ?? []).map((ch: any) => (
                                        <option key={ch.id} value={ch.id}>{ch.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs text-gray-500">Сумма ($) *</label>
                                <input 
                                    type="number" 
                                    step="0.01"
                                    value={expAmount}
                                    onChange={(e) => setExpAmount(e.target.value)}
                                    className="w-full border rounded px-3 py-1.5 text-sm"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs text-gray-500">Дата *</label>
                                <input 
                                    type="date" 
                                    value={expDate}
                                    onChange={(e) => setExpDate(e.target.value)}
                                    className="w-full border rounded px-3 py-1.5 text-sm"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs text-gray-500">Регистраций</label>
                                <input 
                                    type="number" 
                                    value={expRegs}
                                    onChange={(e) => setExpRegs(e.target.value)}
                                    className="w-full border rounded px-3 py-1.5 text-sm"
                                />
                            </div>
                            <button
                                onClick={handleCreateExpense}
                                disabled={expSaving || !expChannelId || !expAmount || !expDate || !expDesc}
                                className="bg-purple-600 text-white px-4 py-1.5 rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                            >
                                {expSaving ? "Сохранение..." : "Добавить"}
                            </button>
                            <div className="sm:col-span-2 lg:col-span-4 space-y-1">
                                <label className="text-xs text-gray-500">Описание *</label>
                                <input 
                                    type="text" 
                                    placeholder="На что потрачено, период и т.д."
                                    value={expDesc}
                                    onChange={(e) => setExpDesc(e.target.value)}
                                    className="w-full border rounded px-3 py-1.5 text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Expenses list */}
                    <div className="bg-white rounded-xl border overflow-hidden">
                        <div className="px-4 py-3 border-b flex justify-between items-center bg-gray-50/50">
                            <span className="font-semibold text-gray-700 flex items-center gap-2 text-sm">
                                <HistoryIcon className="w-4 h-4" />
                                История расходов
                            </span>
                            <div className="flex gap-2 items-center">
                                <select 
                                    value={expensesParams.channel_id ?? ""}
                                    onChange={(e) => setExpensesParams(p => ({ ...p, channel_id: e.target.value ? parseInt(e.target.value) : undefined, page: 1 }))}
                                    className="text-xs border rounded px-2 py-1"
                                >
                                    <option value="">Все каналы</option>
                                    {(Array.isArray(channelsData) ? channelsData : (channelsData as any)?.data ?? []).map((ch: any) => (
                                        <option key={ch.id} value={ch.id}>{ch.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {expensesLoading ? (
                            <div className="text-center py-10 text-gray-400">Загрузка...</div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-left">
                                    <tr>
                                        <th className="px-4 py-3">Канал</th>
                                        <th className="px-4 py-3 text-right">Сумма</th>
                                        <th className="px-4 py-3">Дата</th>
                                        <th className="px-4 py-3">Описание</th>
                                        <th className="px-4 py-3 text-center">Рег.</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y text-gray-600">
                                    {expensesData?.data.map((ex) => (
                                        <tr key={ex.id} className="hover:bg-gray-50/50">
                                            <td className="px-4 py-3 font-medium text-gray-900">{ex.channel?.name || `Канал #${ex.channel_id}`}</td>
                                            <td className="px-4 py-3 text-right font-semibold text-red-600">-${formatAmount(ex.amount)}</td>
                                            <td className="px-4 py-3 text-gray-500">{formatDate(ex.expense_date)}</td>
                                            <td className="px-4 py-3 whitespace-pre-wrap">{ex.description}</td>
                                            <td className="px-4 py-3 text-center font-medium">{ex.registrations_count}</td>
                                        </tr>
                                    ))}
                                    {expensesData?.data.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-8 text-center text-gray-400 italic">Нет записей о расходах</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                        {(expensesData?.last_page ?? 1) > 1 && (
                            <div className="flex justify-between items-center px-4 py-3 border-t text-xs">
                                <button onClick={() => setExpensesParams((p) => ({ ...p, page: (p.page ?? 1) - 1 }))} disabled={(expensesParams.page ?? 1) <= 1} className="px-3 py-1 border rounded disabled:opacity-40">←</button>
                                <span>Стр. {expensesParams.page} / {expensesData?.last_page}</span>
                                <button onClick={() => setExpensesParams((p) => ({ ...p, page: (p.page ?? 1) + 1 }))} disabled={(expensesParams.page ?? 1) >= (expensesData?.last_page ?? 1)} className="px-3 py-1 border rounded disabled:opacity-40">→</button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdvertisingFundPage;
